<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToUpload'
});
</script>
<template>
	<div :id="id" :key="langKey" :flex="flex ? flex : null" class="to-upload" :class="setClass" :style="setStyle">
		<span v-if="verifiable && validateRequired" v-show="false" class="to-validate" :req="validateRequired" :desc="validateDesc" :value="validateValue"></span>
		<input ref="fileInput" type="file" :accept="accept" :multiple="props.multiple" style="display: none" @change="handleChange" />
		<to-input v-if="type === 'input'" link :disabled="props.disabled" :width="props.width" :placeholder="placeholder || t('to.common.placeholder.select')" :verifiable="false">
			<template #suffix>
				<to-icon value="browse" link @click="handleFile"></to-icon>
			</template>
		</to-input>
		<to-button v-if="type === 'button'" :disabled="props.disabled" icon="upload" icon-position="left" @click="handleFile">{{ props.uploadText || t('to.upload.button') }}</to-button>
		<div v-if="type === 'img' && $slots.default" class="to-upload-img" :class="setPicClass">
			<div class="to-upload-add" @click="handleFile">
				<to-icon value="add" mode="upload-add"></to-icon>
			</div>
		</div>
		<div v-if="showList" class="to-upload-list" style="width: 100%">
			<slot :file-list="fileList">
				<div v-if="type === 'img'" class="to-upload-img" :class="setPicClass">
					<div v-if="fileList.length" class="to-upload-img-list">
						<div v-for="(item, index) in fileList" :key="index" class="to-upload-img-list-item">
							<img :src="item.src" class="to-upload-pic" alt="" />
							<to-icon value="delete" mode="icon-remove" link @click="handleDelete(index, item)"></to-icon>
							<div v-if="item.progress" class="upload-progress">
								<div class="upload-progress-bar" :style="{ width: item.progress + '%' }"></div>
							</div>
						</div>
					</div>
					<div v-if="!$slots.default" class="to-upload-add" @click="handleFile">
						<to-icon value="add" mode="upload-add"></to-icon>
					</div>
				</div>
				<template v-else>
					<div v-for="(item, index) in fileList" :key="index" class="to-upload-list-item">
						<to-icon value="img"></to-icon>
						<div v-tip:click="item.name" class="file-name">
							{{ item.name }}
						</div>
						<span>{{ item.status }}</span>
						<div v-if="item.progress" class="upload-progress">
							<div class="upload-progress-bar" :style="{ width: item.progress + '%' }"></div>
						</div>
						<to-icon v-if="item[fileIdText] && downloadable" value="download" mode="color" link @click="handleDownload(item[fileIdText])"></to-icon>
						<to-icon value="delete" mode="color" link @click="handleDelete(index, item)"></to-icon>
					</div>
				</template>
			</slot>
		</div>
		<span v-if="errorTip" v-color="'danger'" class="to-box-tip">
			<template v-if="errorType === 'rule'">
				{{ t('to.upload.selectError') }}
				<to-icon v-tip.danger="errorTip" value="help" color="danger" mode="toast"></to-icon>
			</template>
			<template v-else> {{ t('to.upload.required') }} </template>
		</span>
	</div>
</template>
<script lang="ts" setup>
import { fileAjax, md5 } from '@touchvue/utils'
import { CSSProperties, computed, nextTick, onMounted, ref, inject, watch } from 'vue'
import { ToInput, ToIcon, ToButton, $tip } from '@touchvue/components'
import { t, langKey } from '@touchvue/locale'


const emit = defineEmits(['start', 'success', 'error', 'clear', 'change', 'progress', 'remove', 'download'])

export type UploadType = 'input' | 'img' | 'button'

export interface FileProps {
	flex?: boolean
	mode?: string
	multiple?: boolean
	auto?: boolean
	action?: string
	headers?: object
	placeholder?: string
	desc?: string
	id?: string
	required?: boolean
	type?: UploadType
	picSize?: string | number
	width?: string | number
	maxLength?: number
	accept?: string
	maxSize?: string | number
	duplicatable?: false
	fileIdText?: string
	disabled?: boolean
	downloadable?: boolean
	name?: string
	withCredentials?: boolean
	files?: any[]
	data?: object
	beforeSelect?: ((fileList) => boolean | Promise<any>) | null
	beforeStart?: ((file) => boolean | Promise<any>) | null
	verifiable?: boolean
	showList?: boolean
	uploadText?: string
}

const props = withDefaults(defineProps<FileProps>(), {
	flex: false,
	mode: 'default',
	multiple: false,
	action: '',
	placeholder: '',
	auto: false,
	desc: '',
	id: 'upload' + new Date().getTime(),
	required: false,
	type: 'input',
	picSize: '',
	width: 20,
	maxLength: Infinity,
	accept: props => (props.type === 'img' ? '.jpg,.jpeg,.png,.gif' : ''),
	maxSize: '',
	duplicatable: false,
	fileIdText: 'fileId',
	disabled: false,
	downloadable: false,
	name: 'uploadfile',
	withCredentials: false,
	files: () => [],
	data: () => ({}),
	headers: () => ({}),
	beforeSelect: null,
	beforeStart: null,
	verifiable: true,
	showList: true,
	uploadText: ''
})

const fileInput = ref(null)
const fileList = ref([])
const errorTip = ref('')
const errorType = ref('rule')
const ToFormItem = inject<{ required?: boolean; label?: string }>('ToFormItem', {})
const uploadList = ref([])

onMounted(() => {
	fileList.value = props.files
})
watch(
	() => props.files,
	val => {
		fileList.value = val
	},
	{
		deep: true
	}
)
const setClass = computed(() => {
	const arr = ['to-disable-target']
	if (errorTip.value) {
		arr.push('to-upload-mode-line')
		arr.push('to-upload-color-danger')
		arr.push('to-upload-tip-show')
	} else {
		if (props.mode) {
			arr.push(`mode-${props.mode}`)
		}
	}
	return arr
})
const setPicClass = computed(() => {
	const arr = []
	if (props.picSize) {
		arr.push(`pic-size-${props.picSize}`)
	}
	return arr
})
const setStyle = computed(() => {
	const style: CSSProperties = {}
	if (props.width) {
		style.width = isNaN(Number(props.width)) ? props.width : props.width + 'em'
	}
	return style
})
const validateRequired = computed(() => {
	if (props.required) {
		return 'true'
	} else if (ToFormItem && ToFormItem.required) {
		return 'true'
	} else {
		return 'false'
	}
})
const validateDesc = computed(() => {
	if (props.desc) {
		return props.desc
	} else if (ToFormItem && ToFormItem.label) {
		return ToFormItem.label
	} else {
		return ''
	}
})
const validateValue = computed(() => {
	if (Object.keys(fileList.value).length > 0) {
		return 'true'
	} else {
		return ''
	}
})
function handleFile() {
	if (props.disabled) return false
	fileInput.value.click()
}
async function handleChange(e) {
	let newFileList = []
	newFileList = Array.from(e.target.files)

	errorTip.value = ''
	let flag = true
	if (typeof props.beforeSelect === 'boolean') {
		flag = props.beforeSelect
	} else {
		if (props.beforeSelect) {
			if (typeof props.beforeSelect === 'function') {
				const result = props.beforeSelect(newFileList)
				if (result && typeof (result as Promise<boolean>).then === 'function') {
					flag = await result
				} else {
					flag = !!result
				}
			} else {
				flag = !!props.beforeSelect
			}
		}
	}
	if (!flag) return false
	newFileList.forEach(item => {
		item.uid = getFileUid(item)
	})
	if (fileList.value.length + newFileList.length <= props.maxLength) {
		uploadList.value = Array.from(e.target.files)
		emit('change', newFileList)
		if (props.duplicatable) {
			e.target.value = ''
		}
		if (props.type === 'img') {
			newFileList.forEach(item => {
				let reader = new FileReader()
				reader.addEventListener('load', () => {
					item.src = reader.result
					fileList.value.push(item)
					if (props.auto) {
						handleUpload(item)
					}
				})
				reader.readAsDataURL(item)
			})
		} else {
			fileList.value = [...fileList.value, ...newFileList]
			if (newFileList.length && props.auto) {
				nextTick(() => {
					newFileList.forEach(file => {
						handleUpload(file)
					})
				})
			}
		}
	} else {
		$tip.danger(t('to.upload.tip.error.count', props.maxLength)).catch(() => {})
	}
}

async function handleUpload(file) {
	if (!props.action) return false
	let flag = true
	if (typeof props.beforeStart === 'boolean') {
		flag = props.beforeStart
	} else {
		if (props.beforeStart) {
			if (typeof props.beforeStart === 'function') {
				const result = props.beforeStart(file)
				if (result && typeof (result as Promise<boolean>).then === 'function') {
					flag = await result
				} else {
					flag = !!result
				}
			} else {
				flag = !!props.beforeStart
			}
		}
	}
	if (!flag) return false
	setFilePropertyByUid(file.uid, 'status', 'start')
	emit('start', file)
	let formData = new FormData()
	formData.append(props.name, file)
	if (props.data) {
		Object.keys(props.data).forEach(key => {
			formData.append(key, props.data[key])
		})
	}
	fileAjax({
		action: props.action,
		data: formData,
		headers: props.headers,
		withCredentials: props.withCredentials,
		onError: (err, status) => {
			setFilePropertyByUid(file.uid, 'status', 'error')
			emit('error', err, status, file)
		},
		onSuccess: (data, status) => {
			let _data = JSON.parse(data)
			setFilePropertyByUid(file.uid, 'status', 'success')
			setFilePropertyByUid(file.uid, props.fileIdText, _data[props.fileIdText])
			emit('success', _data, status, file)
		},
		onProgress: e => {
			setFilePropertyByUid(file.uid, 'progress', Math.round((e.loaded / e.total) * 100))
			emit('progress', e.loaded, e.total, file)
		}
	})
}

function handleDelete(index: number, item) {
	if (props.disabled) return false
	let file = fileList.value.splice(index, 1)
	if (fileInput.value.value.includes(item.name)) {
		fileInput.value.value = ''
	}
	emit('remove', file[0])
}
function getFileUid(fileObj) {
	if (!fileObj) return
	let str = (fileObj.lastModifiedDate ? new Date().getTime() : fileObj.modificationdate) + fileObj.name + fileObj.size
	return md5(str)
}
function setFilePropertyByUid(uid, key, value) {
	let idx = fileList.value.findIndex(item => item.uid === uid)
	if (idx === -1) return
	fileList.value[idx][key] = value
	const updatedItem = {
		lastModified: fileList.value[idx].lastModified,
		lastModifiedDate: fileList.value[idx].lastModifiedDate,
		name: fileList.value[idx].name,
		size: fileList.value[idx].size,
		type: fileList.value[idx].type,
		uid: fileList.value[idx].uid,
		src: fileList.value[idx].src,
		status: fileList.value[idx].status,
		progress: fileList.value[idx].progress,
		[props.fileIdText]: fileList.value[idx][props.fileIdText],
		[key]: value
	}
	fileList.value.splice(idx, 1, updatedItem)
}
function handleDownload(fileId) {
	emit('download', fileId)
}
function clear() {
	let tem = fileList.value.filter(item => item[props.fileIdText])
	fileList.value = tem
}
async function submit() {
	errorTip.value = ''
	let flag = true
	if (typeof props.beforeSelect === 'boolean') {
		flag = props.beforeSelect
	} else {
		if (props.beforeSelect) {
			if (typeof props.beforeSelect === 'function') {
				const result = props.beforeSelect(uploadList.value)
				if (result && typeof (result as Promise<boolean>).then === 'function') {
					flag = await result
				} else {
					flag = !!result
				}
			} else {
				flag = !!props.beforeSelect
			}
		}
	}
	if (!flag) return false
	uploadList.value.forEach(item => {
		item.uid = getFileUid(item)
	})
	if (uploadList.value.length <= props.maxLength) {
		uploadList.value.forEach(file => {
			handleUpload(file)
		})
	} else {
		$tip.danger(t('to.upload.tip.error.count', props.maxLength)).catch(() => {})
	}
}
defineExpose({
	clear,
	errorTip,
	errorType,
	submit
})
</script>
